/* eslint-disable react-hooks/exhaustive-deps */

import * as Sentry from '@sentry/nextjs';
import { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { Product as ProductSchema, WithContext } from 'schema-dts';

import { CHANNEL, categoryPageSlugs } from '@boss/constants/b2b-b2c';
import { IProduct, TSUPPORTED_LANGUAGES, color as colorService, product as productService } from '@boss/services';
import { Environment } from '@boss/types/b2b-b2c';
import { getTranslatedslugsForProducts, slugify } from '@boss/utils';

import { SEO } from '../../../components';
import { PageNames, PageTypes } from '../../../constants';
import { ProductDetailPage } from '../../../containers';
import { useColorGroup, useRecentViewedProducts } from '../../../hooks';
import Layout from '../../../layout/default';
import getServerSideTranslations from '../../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocale } from '../../../utils/localeMapper';
import { getMappedNavigation } from '../../../utils/navigation';
import { getBreadcrumbVisibilityForPage } from '../../../utils/options';

// TODO: Develop more. It seems the PHASE_PRODUCTION_BUILD gives a false positive
// due to environment embedding
const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

const IS_DEV = process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER;

export const config = {
  maxDuration: 30,
};

/**
 * Format the slug parameter
 */
export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  try {
    const paths = [];

    // Only generate the paths when dealing with an actual build, ommit on devserver start
    if (IS_BUILD && CHANNEL && locales) {
      for (const locale of locales.filter((locale: string) => locale !== 'default')) {
        const contentfulLocale = mapNextToContentfulLocale(locale);
        // Cast is to play along with the next-i18next config that types locales as a string

        for await (const product of productService.getAll({
          locale: contentfulLocale,
          channel: CHANNEL,
          batchMode: IS_BUILD,
        })) {
          /**
           * we use the product name to generate the slug, as the slug is
           * not available in the product data when creating the redirects maps
           * accessing the pdp with the seo slug is still possible due to the rewrite rules
           */
          paths.push({ locale, params: { slug: slugify(product.name), id: product.id } });
        }
      }
    }

    return {
      paths,
      fallback: IS_DEV ? 'blocking' : false,
    };
  } catch (error) {
    // TODO: Doesn't the next integration with Sentry capture this by default
    // Investigate the practical use of the code below, now re-throwing is
    // re-enabled. See note below.
    Sentry.captureException(error, {
      tags: {
        type: 'Fetching product paths',
      },
    });

    // NOTE: Capturing this without throwing again is dangerous because this will
    // result in all PDP being removed in case of failure
    throw error;
  }
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ id: string; slug: string }>) => {
  const slug = params?.slug;

  if (!slug || !locale || locale === 'default' || !CHANNEL) {
    return {
      notFound: true,
    };
  }

  const contentfulLocale = mapNextToContentfulLocale(locale);

  const [navigation, product] = await Promise.all([
    getMappedNavigation(contentfulLocale, !!preview),
    productService.byId(params.id, contentfulLocale, CHANNEL, IS_BUILD),
  ]);

  if (!product) {
    return {
      notFound: true,
    };
  }

  let colorGroups = null;

  if (product.type !== 'NON_PAINT') {
    colorGroups = await colorService.getColorGroups(contentfulLocale);
  }

  return {
    props: {
      pageType: PageTypes.PRODUCT_DETAIL,
      preview: !!preview,
      navigation,
      slug,
      locale,
      product,
      colorGroups,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'forms', 'product', 'stores', 'routes'])),
    },
  };
};

export const Product = ({
  environment,
  navigation,
  preview,
  product,
  colorGroups,
  locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const { normalizeColorGroupNames } = useColorGroup();
  const { addProductId: addRecentProduct } = useRecentViewedProducts();
  const enrichedGroups = colorGroups ? normalizeColorGroupNames(colorGroups) : null;
  const localizedSlugs = getTranslatedslugsForProducts(product.localizedSlugs, product.id, mapContentfulToNextLocale);

  const getProductCrumbles = (product: IProduct) => {
    try {
      const breadcrumb = getBreadcrumbVisibilityForPage(PageNames.MAIN_CATEGORY_OVERVIEW)
        ? [{ href: categoryPageSlugs[locale], label: t('overview') }]
        : [];
      const productCategory = product.categories[0];

      if (productCategory) {
        for (const crumble of productCategory.breadcrumb) {
          breadcrumb.push({ href: crumble.uri, label: crumble.label });
        }
      }

      breadcrumb.push({
        href: product.localizedSlugs[mapNextToContentfulLocale(locale) as TSUPPORTED_LANGUAGES],
        label: product.seoTitle ?? product.name,
      });

      return breadcrumb;
    } catch (error) {
      // TODO: Warn sentry about incomplete product data
      console.warn(`Could not form breadcrumb for product ${product.id} - ${product.name}`);

      return [{ href: categoryPageSlugs[locale], label: t('overview') }];
    }
  };

  const crumbles = product.categories && getProductCrumbles(product);

  const pageTitle = crumbles.length ? crumbles[crumbles.length - 1].label : t('overview');
  const pageDescription = product.seoDescription ?? product.description ?? pageTitle;

  useEffect(() => {
    addRecentProduct(product.id);
  }, [product.id]);

  const structuredData: WithContext<ProductSchema>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      itemCondition: 'NewCondition',
      description: product.seoDescription ?? product.description ?? '',
      name: product.name,
      image: product.packshot.src ?? '',
      productID: product.id,
      category: product.categories.map(cat => cat.trackingCategories[cat.trackingCategories.length - 1]).join(' '),
      keywords: product.keywords?.join(' ') ?? '',
    },
  ];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      imageSrc={product.packshot?.src}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      structuredData={structuredData}
      title={pageTitle}
    >
      <Layout
        containerWrapperClassName="relative"
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <ProductDetailPage colorGroups={enrichedGroups} product={product} seoTitle={pageTitle} />
      </Layout>
    </SEO>
  );
};

export default Product;
